import styles from "./ReferFriends.module.scss";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import useAppSelector from "../../../hooks/useAppSelector";
import { ReferralProgramService } from "../../../services/referralProgram.service";
import { openErrorMessage, openSuccessMessage } from "../../../store/message/messageSlice";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { environment } from "../../../environment";
import { getLanguageFromLocal, textCropping } from "../../../helper/language";
import ReferHowItWorks from "./ReferHowItWorks/ReferHowItWorks";
import { Form } from "antd";
import FormItemInput from "../../../components/ui/FormItemInput/FormItemInput";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import mailIcon from "../../../assets/images/icons/mail.svg";
import { emailValidation } from "../../../helper/auth";
import { handleError } from "../../../helper/errorHandler";

const ReferFriends = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user } = useAppSelector((state) => state.userProfileSlice);

  const [referralLink, setReferralLink] = useState<string>("");
  const [isCopied, setIsCopied] = useState(false);
  const [shouldUpdateData, setShouldUpdateData] = useState(false);

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 5000);
    }
  }, [isCopied]);

  useEffect(() => {
    if (user) {
      getReferralToken(user.id);
    }
  }, [user]);

  const getReferralToken = async (userId: string) => {
    try {
      const response = await ReferralProgramService.getReferralTokenByUserId(userId);
      if (response.status === 200 && response.data && response.data.token) {
        let link = "";
        if (environment.environment === "development") {
          link = `http://localhost:3000/register?refer=${response.data.token}`;
        } else {
          link = `${window.location.protocol}//${window.location.hostname}/register?refer=${response.data.token}`;
        }
        setReferralLink(link);
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  const clickOnCopy = () => {
    navigator.clipboard.writeText(referralLink).then((res) => {
      setIsCopied(true);
    });
  };

  const onFinish = async (values: { email: string }) => {
    try {
      if (!user) return;

      if (!emailValidation(values.email)) {
        dispatch(openErrorMessage(t("PleaseEnterYourEmailCorrectly")));
        return;
      }

      const model = {
        email: values.email,
        userId: user.id,
        lang: getLanguageFromLocal(),
      };

      const response = await ReferralProgramService.sendReferralInvationOnEmail(model);
      if (response.data && response.status === 200) {
        setShouldUpdateData(!shouldUpdateData);
        dispatch(openSuccessMessage(t("InvitationHasBeenSentToTheMail")));
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t("ReferFriends")}</h2>
      <h3 className={styles.subtitle}>Last chance to score £300</h3>
      <p className={styles.text}>
        Invite your friends to join ClubPass & earn £20 for every one who becomes a paying member.
        Get 3 friends to join & we’ll sweeten the deal to £300. Offer ends Oct 11th.{" "}
        <span>Terms and conditions applied.</span>
      </p>
      <div className={styles.wrapper}>
        <h3 className={styles.subtitle}>Send Invitation</h3>
        <Form
          className={styles.form}
          name="send-refferal-email"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemInput
            name={"email"}
            label={t("Email")}
            rules={{ required: true }}
            placeholder={"Example@gmail.com"}
          />
          <CustomButton
            text={t("SendInvites")}
            type={"submit"}
            typeStyle={"fill"}
            icon={mailIcon}
            altIcon={"send mail"}
          />
        </Form>
        <h3 className={`${styles.subtitle} ${styles.center}`}>or</h3>
        <div className={styles.linkWrapper}>
          {textCropping(referralLink, 35)}
          <button onClick={clickOnCopy}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 12.9V17.1C16 20.6 14.6 22 11.1 22H6.9C3.4 22 2 20.6 2 17.1V12.9C2 9.4 3.4 8 6.9 8H11.1C14.6 8 16 9.4 16 12.9Z"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22 6.9V11.1C22 14.6 20.6 16 17.1 16H16V12.9C16 9.4 14.6 8 11.1 8H8V6.9C8 3.4 9.4 2 12.9 2H17.1C20.6 2 22 3.4 22 6.9Z"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("Copy")}
          </button>
        </div>
        {isCopied && <p className={styles.hint}>{t("YourLinkHasBeenCopied")}</p>}
      </div>
      {!!user && <ReferHowItWorks shouldUpdateData={shouldUpdateData} userId={user.id} />}
    </div>
  );
};

export default ReferFriends;
